import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';

type UnsubscribeState = 'confirm' | 'loading' | 'success' | 'already_unsubscribed' | 'not_found' | 'error';

/**
 * Unsubscribing waits for the button: link scanners and mail prefetchers open
 * links in emails, and must not unsubscribe anyone just by loading the page.
 */
export const EmailUnsubscribePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<UnsubscribeState>(token ? 'confirm' : 'error');

  const unsubscribe = () => {
    setState('loading');
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';

    fetch(`${apiBase}/api/email/unsubscribe/${token}`, { method: 'POST' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setState(data.data?.alreadyUnsubscribed ? 'already_unsubscribed' : 'success');
        } else if (data.error?.code === 'TOKEN_NOT_FOUND') {
          setState('not_found');
        } else {
          setState('error');
        }
      })
      .catch(() => {
        setState('error');
      });
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-sm">
        {state === 'confirm' && (
          <>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Unsubscribe from Wallstreetle emails?</h1>
            <p className="mb-6 text-muted-foreground">
              You'll stop getting our emails. You can still play every day.
            </p>
            <Button onClick={unsubscribe}>Unsubscribe</Button>
          </>
        )}

        {state === 'loading' && (
          <>
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
            <p className="text-muted-foreground">Processing your request...</p>
          </>
        )}

        {state === 'success' && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Unsubscribed</h1>
            <p className="text-muted-foreground">
              You've been successfully unsubscribed from Wallstreetle emails.
            </p>
          </>
        )}

        {state === 'already_unsubscribed' && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Already Unsubscribed</h1>
            <p className="text-muted-foreground">
              You were already unsubscribed from Wallstreetle emails.
            </p>
          </>
        )}

        {state === 'not_found' && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
              <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Link Not Found</h1>
            <p className="text-muted-foreground">
              This unsubscribe link is invalid or has expired.
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">Something Went Wrong</h1>
            <p className="text-muted-foreground">
              We couldn't process your request. Please try again later.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
